import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetStoreInfoQuery, useGetStoreMenuQuery, useCheckoutMutation } from '../../services/api';
import { formatCurrency, calculateCartTotal } from '../../utils/helpers';
import { updateFaviconAndTitle, resetToDefault } from '../../utils/favicon';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './StorePage.css';

const StorePage = () => {
  const { shopSlug } = useParams();
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: ''
  });
  const [isNewCustomer, setIsNewCustomer] = useState(true);

  const { data: storeInfo, isLoading: storeLoading, error: storeError } = useGetStoreInfoQuery(shopSlug);
  const { data: menuData, isLoading: menuLoading, error: menuError } = useGetStoreMenuQuery({ 
    shopSlug, 
    available: 'true' 
  });
  const [checkout, { isLoading: checkoutLoading }] = useCheckoutMutation();

  const store = storeInfo?.data?.restaurant;
  const menu = menuData?.data;
  const categories = menu?.categories || {};

  // Apply store theme

    useEffect(() => {
    if (store) {
      updateFaviconAndTitle(store.logoUrl, store.name);
    }
  }, [store]);

    // Reset to default when component unmounts
  useEffect(() => {
    return () => {
      resetToDefault();
    };
  }, []);

  useEffect(() => {
    if (store?.themeColor) {
      document.documentElement.style.setProperty('--primary-color', store.themeColor);
      document.documentElement.style.setProperty('--primary-dark', darkenColor(store.themeColor, 0.2));
    }
  }, [store?.themeColor]);

  const darkenColor = (color, amount) => {
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);
    
    r = Math.floor(r * (1 - amount));
    g = Math.floor(g * (1 - amount));
    b = Math.floor(b * (1 - amount));
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(cartItem => cartItem.menuItemId === item._id);
      if (existing) {
        return prev.map(cartItem =>
          cartItem.menuItemId === item._id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prev, {
          menuItemId: item._id,
          name: item.name,
          price: item.price,
          quantity: 1
        }];
      }
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      removeFromCart(itemId);
    } else {
      setCart(prev =>
        prev.map(item =>
          item.menuItemId === itemId
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.menuItemId !== itemId));
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return calculateCartTotal(cart);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    // For returning customers, we might have their info stored
    const storedCustomer = localStorage.getItem(`customer_${shopSlug}`);
    if (storedCustomer) {
      const customer = JSON.parse(storedCustomer);
      setCustomerData(customer);
      setIsNewCustomer(false);
      await placeOrder(customer, false);
    } else {
      setShowCheckout(true);
    }
  };

  const placeOrder = async (customerData, isNew = true) => {
    try {
      const orderData = {
        items: cart.map(item => ({
          menuItemId: item.menuItemId,
          price: item.price,
          quantity: item.quantity
        })),
        customer: customerData,
        isNewCustomer: isNew
      };

      const result = await checkout({ shopSlug, ...orderData }).unwrap();
      
      if (result.success) {
        // Store customer info for future orders
        if (isNew) {
          localStorage.setItem(`customer_${shopSlug}`, JSON.stringify(customerData));
        }
        
        // Clear cart and show success
        setCart([]);
        setShowCheckout(false);
        alert(`Order placed successfully! Your order number is: ${result.data.order.orderNumber}`);
      }
    } catch (error) {
      alert(error.data?.message || 'Failed to place order. Please try again.');
    }
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    await placeOrder(customerData, isNewCustomer);
  };

  if (storeLoading || menuLoading) {
    return <Loading text="Loading restaurant..." />;
  }

  if (storeError) {
    return (
      <Error 
        message="Restaurant not found or inactive" 
        retryText="Go Home"
        onRetry={() => window.location.href = '/'}
      />
    );
  }

  if (menuError) {
    return <Error message="Failed to load menu" />;
  }

  return (
    <div className="storefront">
      {/* Store Header */}
      <header className="store-header" style={{ backgroundColor: store?.themeColor }}>
        <div className="container">
          <div className="store-info">
            {store?.logoUrl && (
              <img 
                src={`${store.logoUrl}`} 
                alt={store.name}
                className="store-logo"
              />
            )}
            <div className="store-details">
              <h1 className="store-name">{store?.name}</h1>
              <p className="store-address">{store?.address}</p>
            </div>
          </div>
          <div className="store-actions">
            <a href={`/${shopSlug}/orders`} className="btn btn-light">
              My Orders
            </a>
          </div>
        </div>
      </header>

      {/* Menu Sections */}
      <main className="store-main">
        <div className="container">
          {Object.keys(categories).length === 0 ? (
            <div className="empty-menu">
              <h2>No menu items available</h2>
              <p>This restaurant hasn't added any items to their menu yet.</p>
            </div>
          ) : (
            Object.entries(categories).map(([category, items]) => (
              <section key={category} className="menu-section">
                <h2 className="category-title">{category}</h2>
                <div className="menu-grid">
                  {items.map((item) => (
                    <div key={item._id} className="menu-item-card">
                      {item.imageUrl && (
                        <img 
                          src={`${item.imageUrl}`} 
                          alt={item.name}
                          className="item-image"
                        />
                      )}
                      <div className="item-details">
                        <h3 className="item-name">{item.name}</h3>
                        <p className="item-price">{formatCurrency(item.price)}</p>
                        <button
                          onClick={() => addToCart(item)}
                          className="btn btn-primary btn-sm"
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </main>

      {/* Cart Sidebar */}
      {cart.length > 0 && (
        <div className="cart-sidebar">
          <div className="cart-header">
            <h3>Your Order ({getCartItemCount()} items)</h3>
            <button 
              onClick={() => setCart([])}
              className="btn btn-sm btn-danger"
            >
              Clear
            </button>
          </div>
          
          <div className="cart-items">
            {cart.map((item) => (
              <div key={item.menuItemId} className="cart-item">
                <div className="item-info">
                  <span className="item-name">{item.name}</span>
                  <span className="item-price">{formatCurrency(item.price)}</span>
                </div>
                <div className="quantity-controls">
                  <button
                    onClick={() => updateQuantity(item.menuItemId, item.quantity - 1)}
                    className="quantity-btn"
                  >
                    -
                  </button>
                  <span className="quantity">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.menuItemId, item.quantity + 1)}
                    className="quantity-btn"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => removeFromCart(item.menuItemId)}
                  className="remove-btn"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
          
          <div className="cart-footer">
            <div className="cart-total">
              Total: <strong>{formatCurrency(getCartTotal())}</strong>
            </div>
            <button
              onClick={handleCheckout}
              className="btn btn-primary btn-lg w-full"
              disabled={checkoutLoading}
            >
              {checkoutLoading ? 'Placing Order...' : 'Checkout'}
            </button>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Complete Your Order</h2>
              <button 
                onClick={() => setShowCheckout(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCheckoutSubmit} className="modal-body">
              <div className="order-summary">
                <h3>Order Summary</h3>
                {cart.map(item => (
                  <div key={item.menuItemId} className="order-item">
                    <span>{item.name} × {item.quantity}</span>
                    <span>{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
                <div className="order-total">
                  <strong>Total: {formatCurrency(getCartTotal())}</strong>
                </div>
              </div>

              <div className="customer-form">
                <h3>Customer Information</h3>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Mobile Number *</label>
                  <input
                    type="tel"
                    value={customerData.mobile}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, mobile: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Delivery Address *</label>
                  <textarea
                    value={customerData.address}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, address: e.target.value }))}
                    className="form-control"
                    rows="3"
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCheckout(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePage;